import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import makeAsyncScriptLoader from 'react-async-script';

import { useSoyuzClientConfig, useSoyuzLocales } from '@spotahome/soyuz/client';

import { useThemeContext } from '../..';

const callbackName = 'onloadcallback';
const globalName = 'grecaptcha';

const noOp = () => {};

const ReCaptcha = ({
  onChange,
  onExpired,
  onError = noOp,
  grecaptcha = null
}) => {
  const containerRef = useRef(null);
  const recaptchaIdRef = useRef(null);
  const executeRequestedRef = useRef(false);
  const { theme } = useThemeContext();
  const { current: currentLocale } = useSoyuzLocales();
  const { recaptchaSiteKeys } = useSoyuzClientConfig();

  const getCaptchaFunction = fnName => grecaptcha?.[fnName] || null;

  // eslint-disable-next-line consistent-return
  const executeCaptcha = () => {
    const execute = getCaptchaFunction('execute');
    if (execute && recaptchaIdRef.current !== undefined) {
      return execute(recaptchaIdRef.current);
    }
    executeRequestedRef.current = true;
  };

  useEffect(() => {
    const render = getCaptchaFunction('render');
    if (render && !recaptchaIdRef.current) {
      const wrapper = document.createElement('div');
      recaptchaIdRef.current = render(wrapper, {
        sitekey: recaptchaSiteKeys[theme] || recaptchaSiteKeys.spotahome,
        callback: onChange,
        theme: 'light',
        type: 'image',
        tabindex: 0,
        'expired-callback': onExpired,
        'error-callback': onError,
        size: 'normal',
        // stoken: this.props.stoken,
        hl: currentLocale,
        badge: 'bottomright',
        isolated: false
      });
      containerRef.current.appendChild(wrapper);
    }
    if (executeRequestedRef.current && grecaptcha && recaptchaIdRef.current) {
      executeRequestedRef.current = false;
      executeCaptcha();
    }
  }, [grecaptcha]);

  return <div ref={containerRef} />;
};

ReCaptcha.propTypes = {
  onChange: PropTypes.func.isRequired,
  onExpired: PropTypes.func.isRequired,
  onError: PropTypes.func,
  grecaptcha: PropTypes.shape({
    execute: PropTypes.func,
    getPageId: PropTypes.func,
    getResponse: PropTypes.func,
    ready: PropTypes.func,
    render: PropTypes.func,
    reset: PropTypes.func
  })
};

function getOptions() {
  return (typeof window !== 'undefined' && window.recaptchaOptions) || {};
}

function getURL() {
  const hostname = 'www.google.com';
  return `https://${hostname}/recaptcha/api.js?onload=${callbackName}&render=explicit`;
}

export default makeAsyncScriptLoader(getURL, {
  callbackName,
  globalName,
  attributes: getOptions().nonce ? { nonce: getOptions().nonce } : {}
})(ReCaptcha);
